import { isMobile } from '@utilities/theme';

/**
 * PriceRange - Custom element for handling price range filter inputs
 *
 * Manages min/max price input validation and ensures logical price ranges
 * Automatically adjusts values to prevent invalid range selections
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/**
 * FacetRemove - Simplified component for handling filter removal links
 *
 * Intercepts click events on filter removal links and dispatches custom events
 * for FilterDrawer to handle URL updates and state management
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      // Dispatch custom event for FilterDrawer to handle removal
      this.dispatchEvent(new CustomEvent('facet-remove:clicked', {
        detail: { originalEvent: event },
        bubbles: true
      }));
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 * FeaturedFilters - Component for managing sticky filter buttons and drawer coordination
 *
 * This component handles the featured filter buttons that appear at the top of collection/search pages
 * and provides sticky behavior on mobile. It coordinates with the FilterDrawer component to maintain
 * consistent filter state across different UI elements.
 *
 * Key Features:
 * - Sticky filter behavior on mobile using Intersection Observer
 * - Bidirectional synchronization with FilterDrawer component
 * - URL state initialization and maintenance
 * - Legacy facets.js compatibility for server-side filtering
 * - Responsive header height adjustment
 *
 * Behavior:
 * - Detects when FilterDrawer system is active and delegates filter handling
 * - Falls back to legacy form-based filtering for server-side implementations
 * - Automatically syncs button states with current URL parameters
 * - Provides sticky positioning when scrolled past original position
 */
class FeaturedFilters extends HTMLElement {
  constructor() {
    super();
  }

  allowDebug() {
    return this.classList.contains('debug');
  }

  connectedCallback() {
    this.init();
    this.initStickyFilter();
  }

  /**
   * Debug logging utility - only logs when debug mode is enabled
   * @param {string} message - The debug message
   * @param {...any} args - Additional arguments to log
   * @returns {void}
   * @private
   */
  debugLog(message, ...args) {
    if(!this.allowDebug()) return;
    console.log(`[STICKY FILTER DEBUG] ${message}`, ...args);
  }

  init() {
    this.initElements();
    this.initEventListeners();
    this.initializeFromUrl();
  }

  initElements() {
    this.options = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    this.optionLinks = Array.from(this.querySelectorAll('label a'));
    // Find the filters form in the document (now a regular form element)
    this.filtersForm = document.querySelector('form.facet-filters-form');
    // Find the filter drawer
    this.filterDrawer = document.querySelector('filter-drawer');
  }

  initEventListeners() {
    this.addEventListener("change", (event) => {
      if (event.target.matches('input[type="checkbox"]')) {
        this.handleCheckboxChange(event.target);
      }
    });
  }

  /**
   * Move filter opener to header on mobile when product grid reaches viewport top
   * This eliminates gap issues by making the filter opener part of the header layout
   * @returns {void}
   * @private
   */
  initStickyFilter() {
    const filterOpener = this.querySelector('.slide-out-drawer-opener.filter-opener');

    this.debugLog('Elements found:', {
      featuredFilters: !!this,
      filterOpener: !!filterOpener,
      featuredFiltersElement: this,
      filterOpenerElement: filterOpener
    });

    if (!filterOpener) {
      this.debugLog('Missing required elements, exiting');
      return;
    }

    // Find elements needed for positioning
    const siteHeader = document.getElementById('site-header');
    const productGrid = document.querySelector('[data-product-grid-container]');
    let headerFilterContainer = siteHeader?.querySelector('.header-filter-container');
    // Store original position for restoration
    const originalParent = filterOpener.parentNode;
    const originalNextSibling = filterOpener.nextSibling;
    // Find the existing placeholder rendered server-side
    const placeholder = originalParent?.querySelector('.filter-opener-placeholder');

    if (!siteHeader || !productGrid || !headerFilterContainer || !placeholder) {
      this.debugLog('Missing required elements (site-header, product-grid, header-filter-container, or filter-opener-placeholder), exiting');
      return;
    }

    let isInHeader = false;

    /* Create intersection observer for product grid */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const screenWidth = window.innerWidth;
          const gridRect = entry.boundingClientRect;

          // Get current header height for threshold calculation
          const headerHeight = getComputedStyle(document.body).getPropertyValue('--header-height');
          const parsedHeaderHeight = headerHeight ? parseInt(headerHeight, 10) || 0 : 0;

          // Trigger when grid top reaches header bottom (viewport top + header height)
          const isGridTopAtHeaderBottom = gridRect.top <= parsedHeaderHeight && entry.isIntersecting;

          this.debugLog('Product grid intersection:', {
            isIntersecting: entry.isIntersecting,
            screenWidth: screenWidth,
            headerHeight: parsedHeaderHeight,
            gridTop: gridRect.top,
            isGridTopAtHeaderBottom: isGridTopAtHeaderBottom,
            shouldApply: isMobile(),
            isInHeader: isInHeader,
            boundingClientRect: entry.boundingClientRect,
            intersectionRect: entry.intersectionRect
          });

          if (!isMobile()) {
            // Desktop: ensure filter opener is in original position
            if (isInHeader) {
              if (originalParent && originalNextSibling) {
                originalParent.insertBefore(filterOpener, originalNextSibling);
              } else if (originalParent) {
                originalParent.appendChild(filterOpener);
              }
              placeholder.style.display = 'none';
              isInHeader = false;
              this.debugLog('Moved filter opener back to original position (desktop)');
            }
            return;
          }

          // Mobile: move to header when product grid top reaches viewport top
          this.debugLog('Grid position check:', {
            rectTop: gridRect.top,
            headerHeight: parsedHeaderHeight,
            isGridTopAtHeaderBottom: isGridTopAtHeaderBottom,
            isIntersecting: entry.isIntersecting
          });

          if (isGridTopAtHeaderBottom && !isInHeader) {
            // Product grid top has reached header bottom - move filter to header
            headerFilterContainer.appendChild(filterOpener);
            placeholder.style.display = 'block';
            isInHeader = true;
            this.debugLog('Moved filter opener to header (grid at top)');
          } else if (gridRect.top > 0 && isInHeader) {
            // Product grid has moved back down - move filter back to original position
            if (originalParent && originalNextSibling) {
              originalParent.insertBefore(filterOpener, originalNextSibling);
            } else if (originalParent) {
              originalParent.appendChild(filterOpener);
            }
            placeholder.style.display = 'none';
            isInHeader = false;
            this.debugLog('Moved filter opener back to original position (grid moved down)');
          }
        });
      },
      {
        // Observe when element comes into view
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      }
    );

    observer.observe(productGrid);
    this.debugLog('Started observing product grid for filter opener positioning');

    // Also add scroll listener as backup to ensure we catch position changes
    let scrollTimeout;
    const checkPosition = () => {
      if (!isMobile()) return; // Only on mobile

      const rect = productGrid.getBoundingClientRect();
      const headerHeight = getComputedStyle(document.body).getPropertyValue('--header-height');
      const parsedHeaderHeight = headerHeight ? parseInt(headerHeight, 10) || 0 : 0;
      const isGridTopAtHeaderBottom = rect.top <= parsedHeaderHeight;

      if (isGridTopAtHeaderBottom && !isInHeader) {
        headerFilterContainer.appendChild(filterOpener);
        placeholder.style.display = 'block';
        isInHeader = true;
      } else if (rect.top > 50 && isInHeader) { // Add some hysteresis
        if (originalParent && originalNextSibling) {
          originalParent.insertBefore(filterOpener, originalNextSibling);
        } else if (originalParent) {
          originalParent.appendChild(filterOpener);
        }
        placeholder.style.display = 'none';
        isInHeader = false;
      }
    };

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkPosition, 16); // ~60fps
    });
  }

  /**
   * Handle checkbox state changes for filter buttons
   * Delegates to FilterDrawer system when active, falls back to legacy form handling
   * @param {HTMLInputElement} option - The checkbox that was changed
   */
  async handleCheckboxChange(option) {
    // Check if filter drawer system is active and should handle this instead
    if (this.filterDrawer && this.filterDrawer.classList.contains('filter-drawer--initialized')) {
      this.debugLog('FeaturedFilters: FilterDrawer system is active, ensuring drawer is ready for filter sync');

      // If the filter drawer hasn't rendered its content yet, we need to trigger it
      if (!this.filterDrawer.filtersRendered) {
        this.debugLog('FeaturedFilters: Filter drawer not rendered yet, triggering render');
        await this.ensureFilterDrawerRendered();
      }

      // Now let the filter drawer handle the change by dispatching to it directly
      this.handleFilterDrawerChange(option);
      return;
    }

    // Legacy behavior when filter drawer is not active
    const type = option.dataset.type;
    const values = option.dataset.value.split(',');
    let triggerChange = false;
    if (!this.filtersForm) {
      console.error("No filters form found");
      return;
    }
    // Since filtersForm is now the actual form element, use it directly
    const form = this.filtersForm;
    values.forEach((value) => {
      const targetEl = form.querySelector(`input[name="${type}"][data-value="${value}"]`);
      if (targetEl) {
        targetEl.checked = option.checked;
        triggerChange = true;
        // Dispatch both input and change events
        targetEl.dispatchEvent(new Event('input', { bubbles: true }));
        targetEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (triggerChange) {
      // Dispatch events on the form itself
      form.dispatchEvent(new Event('input', { bubbles: true }));
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Ensure the filter drawer has rendered its content
   * @returns {Promise<void>}
   */
  async ensureFilterDrawerRendered() {
    if (!this.filterDrawer) {
      console.warn('FeaturedFilters: No filter drawer found');
      return;
    }

    // If already rendered, nothing to do
    if (this.filterDrawer.filtersRendered) {
      return;
    }

    // Check if the drawer should auto-load data
    if (!this.filterDrawer.shouldAutoLoad || !this.filterDrawer.shouldAutoLoad()) {
      console.warn('FeaturedFilters: Filter drawer should not auto-load');
      return;
    }

    // Show loading state
    if (this.filterDrawer.showLoadingState) {
      this.filterDrawer.showLoadingState();
    }

    try {
      // Load filter data
      if (this.filterDrawer.loadFilterData) {
        await this.filterDrawer.loadFilterData();
      }

      // Render filters
      if (this.filterDrawer.state && this.filterDrawer.state.data && this.filterDrawer.state.data.filters) {
        if (this.filterDrawer.renderFilters) {
          this.filterDrawer.renderFilters(this.filterDrawer.state.data.filters);
        }
      } else {
        if (this.filterDrawer.renderEmptyFilters) {
          this.filterDrawer.renderEmptyFilters();
        }
      }
    } catch (error) {
      console.error('FeaturedFilters: Failed to render filter drawer:', error);
      if (this.filterDrawer.showErrorState) {
        this.filterDrawer.showErrorState('Failed to load filters');
      }
    }
  }

  /**
   * Handle filter change when filter drawer is active
   * @param {HTMLInputElement} option - The checkbox that was changed
   */
  handleFilterDrawerChange(option) {
    const filterName = option.dataset.type;
    const filterValue = option.dataset.value;
    const action = option.checked ? 'add' : 'remove';

    this.debugLog('FeaturedFilters: Handling filter drawer change', { filterName, filterValue, action });

    // Update the filter drawer's internal state directly
    if (this.filterDrawer && this.filterDrawer.handleFilterChange) {
      this.filterDrawer.handleFilterChange(filterName, filterValue, action);
    } else {
      // Fallback: dispatch the filter change event
      const event = new CustomEvent('featured-filters:filter-change', {
        detail: { filterName, filterValue, action },
        bubbles: true,
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Initialize filter button states from URL parameters
   */
  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = {};

    // Parse active filters from URL
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.')) {
        if (!activeFilters[key]) {
          activeFilters[key] = [];
        }
        activeFilters[key].push(value);
      }
    }

    // Update button states to reflect URL state
    this.updateButtonStates(activeFilters);
  }

  /**
   * Update filter button states to reflect current filter state
   * @param {Object} activeFilters - Current active filters from filter drawer
   */
  updateButtonStates(activeFilters = {}) {
    this.options.forEach((checkbox) => {
      const filterName = checkbox.dataset.type;
      const filterValue = checkbox.dataset.value;

      // Check if this filter should be active
      let shouldBeChecked = false;
      if (activeFilters[filterName] && Array.isArray(activeFilters[filterName])) {
        shouldBeChecked = activeFilters[filterName].includes(filterValue);
      }

      // Update checkbox state
      checkbox.checked = shouldBeChecked;

      // Update visual state of the containing button group
      const buttonGroup = checkbox.closest('.filter__buttons');
      if (buttonGroup) {
        const hasAnyChecked = buttonGroup.querySelectorAll('input[type="checkbox"]:checked').length > 0;
        buttonGroup.dataset.active = hasAnyChecked ? 'true' : '';
      }
    });
  }
}

customElements.define('featured-filters', FeaturedFilters);
